import {
  Add,
  EditNoteOutlined,
  EditNoteRounded,
  LocalAtmOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as CartActions from "../../../../redux/cart/cart.actions";
import * as CartReducers from "../../../../redux/cart/cart.reducers";
import { clearCartAction } from "../../../../redux/cart/cart.reducers";
import { CartReduxService } from "../../../../redux/cart/CartReduxService";
import * as OrderActions from "../../../../redux/orders/orders.actions";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import * as UserActions from "../../../../redux/users/users.actions";
import * as UserReducers from "../../../../redux/users/users.reducers";
import CustomTooltip from "../../../../utils/CustomTooltip";
import { ToastUtil } from "../../../../utils/ToastUtil";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import {
  IOrderRequestView,
  OrderReqProductsEntity,
} from "../../../orders/models/IOrderRequestView";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import { AddressDisplayText } from "../../../users/pages/user-profile/UserProfile";
import CheckoutButton from "../../components/checkout-button/CheckoutButton";
import { TheProduct } from "../../models/ICartResponseView";
import { primary } from "../../../../themes/theme";

function CheckoutText(props: any) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        mx: 2,
      }}
    >
      <Typography
        sx={{
          fontWeight: "400",
          fontFamily: "Ubuntu",
        }}
        variant="body1"
      >
        {props.primary}
      </Typography>
      <Typography
        sx={{
          fontWeight: "bold",
          fontFamily: "Ubuntu",
        }}
        variant="body2"
      >
        {props.secondary}
      </Typography>
    </Box>
  );
}

const CartCheckout: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const [toastMessage, setToastMessage] = useState<string>("");

  //Get Use Data from redux store
  const userReduxState: UserReducers.InitialState = useSelector(
    (state: RootState) => {
      return state[UserReducers.userFeatureKey];
    }
  );

  //Get Use Data from redux store
  const cartReduxState: CartReducers.InitialState = useSelector(
    (state: RootState) => {
      return state[CartReducers.cartFeatureKey];
    }
  );

  const { loading, address, user } = userReduxState;
  const { loading: cartLoading, cartProduct } = cartReduxState;

  useEffect(() => {
    dispatch(UserActions.getAddressAction());
    dispatch(CartActions.getCartInfoAction());
  }, []);

  const getOrderProducts = (
    products: TheProduct[]
  ): OrderReqProductsEntity[] => {
    return products.map((product) => {
      return {
        product: product._id,
        count: product.count,
        price: product.price,
      };
    });
  };

  //Handle Place Order
  const handlePlaceOder = () => {
    if (!address || Object.keys(address).length === 0) {
      ToastUtil.displayErrorToast(
        "Please add a shipping address before placing an order."
      );
      return;
    }

    if (user._id) {
      const order: IOrderRequestView = {
        products: getOrderProducts(cartProduct.products),
        total: CartReduxService.calculateTotalUtil(
          cartProduct.products
        ).toFixed(2),
        tax: CartReduxService.calculateTaxUtil(cartProduct.products).toFixed(2),
        grandTotal: CartReduxService.calculateGrandTotal(
          cartProduct.products
        ).toFixed(2),
        orderBy: user._id,
        paymentType: "COD",
      };
      dispatch(OrderActions.placeOrderAction({ order: order })).then(
        (response: any) => {
          if (response && !response.error) {
            navigate("/orders/details");
            dispatch({
              type: `${clearCartAction}`,
            });
          }
        }
      );
    }
  };

  return (
    <>
      <Box sx={{ pb: 2, mb: 2 }}>
        {(loading || cartLoading) && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="Checkout"
          description="Securely finalize purchases, review order summary, and complete payment."
        />
        <Grid
          container
          sx={{ gap: "10px", pl: { xs: 1, md: 9, lg: 10 }, pr: 1 }}
        >
          <Grid
            component={Paper}
            elevation={0}
            item
            xs={12}
            md={4}
            sx={{
              height: "100%",
              position: { xs: "none", md: "sticky" },
              top: 90,
              borderRadius: "10px",
            }}
          >
            <Card elevation={0} sx={{ width: "100%", p: 0, m: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  height: "100%",
                  width: "100%",
                  alignItems: "center",
                }}
              >
                {address && Object.keys(address).length > 0 ? (
                  <>
                    <Typography
                      sx={{
                        // textAlign: "center",
                        // background: "#000048",
                        // color: "white",
                        fontFamily: "Ubuntu",
                        pt: 1,
                        fontSize: "18px",
                        letterSpacing: "1px",
                        ml: 2,
                        fontWeight: "bold",
                      }}
                    >
                      | Shipping Address
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        pt: 1,
                        pr: 2,
                        gap: 1,
                      }}
                    >
                      <CustomTooltip title="Edit Address" placement="top">
                        <IconButton
                          size="small"
                          sx={{
                            background: "transparent",
                            color: "green",
                            borderRadius: "7px",
                            width: "25px",
                            height: "25px",
                            border: "2px solid green",
                            "&:hover": {
                              background: "green",
                              color: "white",
                            },
                          }}
                          onClick={() =>
                            navigate(
                              `/users/edit-shipping-address/${address._id}`
                            )
                          }
                        >
                          <EditNoteOutlined fontSize="small" />
                        </IconButton>
                      </CustomTooltip>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{
                        // textAlign: "center",
                        // background: "#000048",
                        // color: "white",
                        fontFamily: "Ubuntu",
                        pt: 1,
                        fontSize: "18px",
                        letterSpacing: "1px",
                        width: "100%",
                        ml: 2,
                        fontWeight: "bold",
                      }}
                    >
                      Add Shipping Address
                    </Typography>
                    <CustomTooltip title="Add Address" placement="top">
                      <IconButton
                        size="small"
                        sx={{
                          background: "transparent",
                          color: "green",
                          borderRadius: "7px",
                          width: "25px",
                          height: "25px",
                          border: "2px solid green",
                          mr: 2,
                          mt: 1,
                          "&:hover": {
                            background: "green",
                            color: "white",
                          },
                        }}
                        onClick={() => navigate("/users/add-shipping-address/")}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </CustomTooltip>
                  </>
                )}
              </Box>
              <Divider
                sx={{
                  mt: 1,
                  mb: 0,
                  color: primary[900],
                  border: "1px dashed #f1f2f3",
                }}
              />
              <CardContent>
                {address && Object.keys(address).length > 0 ? (
                  <React.Fragment key={address._id}>
                    <AddressDisplayText
                      primary={"Name"}
                      secondary={address?.name}
                    />
                    <AddressDisplayText
                      primary={"Email"}
                      secondary={address?.email}
                    />
                    <AddressDisplayText
                      primary={"Mobile"}
                      secondary={address?.mobile}
                    />
                    <AddressDisplayText
                      primary={"Flat"}
                      secondary={address?.flat}
                    />
                    <AddressDisplayText
                      primary={"Street"}
                      secondary={address?.street}
                    />
                    <AddressDisplayText
                      primary={"Landmark"}
                      secondary={address?.landmark}
                    />
                    <AddressDisplayText
                      primary={"City"}
                      secondary={address?.city}
                    />
                    <AddressDisplayText
                      primary={"State"}
                      secondary={address?.state}
                    />
                    <AddressDisplayText
                      primary={"Country"}
                      secondary={address?.country}
                    />
                    <AddressDisplayText
                      primary={"Pin Code"}
                      secondary={address?.pinCode}
                    />
                  </React.Fragment>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontFamily: "Ubuntu",
                        fontWeight: "400",
                        textAlign: "center",
                      }}
                    >
                      No Shipping Address Available!
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {cartProduct && Object.keys(cartProduct).length > 0 && (
            <Grid
              component={Paper}
              key={cartProduct._id}
              elevation={0}
              item
              xs={12}
              md={7}
              lg={7}
              sx={{ borderRadius: "10px" }}
            >
              <Card
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignContent: "space-between",
                  height: "100%",
                }}
              >
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      height: "100%",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Ubuntu",
                        fontSize: "17px",
                        letterSpacing: "1px",
                        pl: 2,
                        pb: 0,
                        mb: 0,
                        fontWeight: "550",
                      }}
                    >
                      | Your Cart Items
                    </Typography>
                    <CustomTooltip title="Edit Items" placement="top">
                      <IconButton
                        size="small"
                        sx={{
                          background: "transparent",
                          color: "green",
                          borderRadius: "7px",
                          width: "25px",
                          height: "25px",
                          border: "2px solid green",
                          mr: 2,
                          "&:hover": {
                            background: "green",
                            color: "white",
                          },
                        }}
                        onClick={() => navigate("/cart/list")}
                      >
                        <EditNoteRounded fontSize="small" />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                  <Divider
                    sx={{
                      color: primary[900],
                      border: "1px dashed #f1f2f3",
                    }}
                  />
                  <CardContent>
                    {cartProduct.products &&
                      cartProduct.products.map((product, index) => {
                        return (
                          <React.Fragment key={index}>
                            <Grid key={index} container spacing={2}>
                              <Grid
                                item
                                xs={3}
                                sx={{
                                  p: 0,
                                  m: "auto",
                                  height: "100%",
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={product?.imageUrl}
                                  alt="no img"
                                  style={{
                                    width: "115px",
                                    height: "110px",
                                    borderRadius: "9px",
                                  }}
                                />
                              </Grid>
                              <Grid item xs={9}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxHeight: "3em",
                                    lineHeight: "1.5em",
                                    fontFamily: "Ubuntu",
                                    fontWeight: "500",
                                  }}
                                >
                                  {product.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: "Ubuntu" }}
                                >
                                  Qty : <strong>{product?.count}</strong>
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: "Ubuntu" }}
                                >
                                  Price :{" "}
                                  <strong>
                                    &#8377;{product?.price?.toFixed(2)}
                                  </strong>
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: "Ubuntu" }}
                                >
                                  Total :{" "}
                                  <strong>
                                    &#8377;
                                    {(product?.price * product?.count)?.toFixed(
                                      2
                                    )}
                                  </strong>
                                </Typography>
                              </Grid>
                            </Grid>
                            <Divider sx={{ my: 1 }} />
                          </React.Fragment>
                        );
                      })}
                    <CheckoutText
                      primary="Total"
                      secondary={`₹${CartReduxService.calculateTotalUtil(
                        cartProduct.products
                      ).toFixed(2)}`}
                    />
                    <CheckoutText
                      primary={"Tax"}
                      secondary={`₹${CartReduxService.calculateTaxUtil(
                        cartProduct.products
                      ).toFixed(2)}`}
                    />
                    <CheckoutText
                      primary={"Delivery Charges"}
                      secondary={
                        <>
                          <span
                            style={{
                              textDecoration: "line-through",
                              fontWeight: "450",
                              marginRight: "3px",
                            }}
                          >
                            ₹40
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              color: "green",
                              fontWeight: "500",
                            }}
                          >
                            Free
                          </span>
                        </>
                      }
                    />
                    <CheckoutText
                      primary={"Payment Type"}
                      secondary={
                        <span style={{ fontSize: "15px" }}>
                          {"Cash On Delivery"}
                        </span>
                      }
                    />
                    <CheckoutText
                      primary={"Grand Total"}
                      secondary={
                        <span style={{ fontSize: "15px" }}>
                          {`₹${CartReduxService.calculateGrandTotal(
                            cartProduct.products
                          ).toFixed(2)}`}
                        </span>
                      }
                    />
                    <Divider variant="middle" sx={{ mb: 1, pb: 2 }} />
                    <CheckoutButton
                      onClick={handlePlaceOder}
                      primaryText={"Place Order"}
                      secondaryText={
                        <Typography
                          sx={{ fontFamily: "Ubuntu", fontWeight: "550" }}
                        >
                          Price : &#8377;
                          {CartReduxService.calculateGrandTotal(
                            cartProduct.products
                          ).toFixed(2)}
                        </Typography>
                      }
                      buttonIcon={<LocalAtmOutlined />}
                    />
                  </CardContent>
                </>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default CartCheckout;
